import moment from "moment-timezone";
import _ from "lodash";

import { SupportTicketStatus } from "src/modules/support/api/support.api";
import { TotalTicketsByStatusResult } from "src/core/dashboard/api/widget-data.api";

import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import { TotalTicketsByStatusGraphValue } from "../TotalTicketsByStatusWidget";

/**
 * Helper to group total tickets by status based on xAxisBy preference
 *
 * @param totalTicketsByStatus A list of flat total tickets by status data
 * @param displayRange DisplayRangeType
 * @param xAxisBy  "date" | "status"
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const totalTicketsByStatus = [
 *  {
      "status": "resolved_by_admin",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1
    },
    {
      "status": "resolved_by_admin",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1
    },
    {
      "status": "resolved_by_admin",
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1
    }
 * ]
 * const displayRange = "month"
 * const xAxisBy = "date"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "resolved_by_admin": 2
    },
    {
      "date": "2022-02-01T00:00:00.000Z",
      "resolved_by_admin": 1
    }
  ]
 * ```
 */
export const groupTotalTicketsByStatus = (totalTicketsByStatus: TotalTicketsByStatusResult, xAxisBy: "date" | "status", displayRange?: DisplayRangeType): TotalTicketsByStatusGraphValue[] => {
  const result: TotalTicketsByStatusGraphValue[] = [];
  // Group the data based on xAxisBy value
  const groupedStatusesData = xAxisBy === "date" && displayRange ? groupByDisplayRange(totalTicketsByStatus, displayRange) : _.groupBy(totalTicketsByStatus, "status");

  // Loop grouped data to form the result, ensuring to include xAxisBy property and all relevant values accumulated
  for (const xAxisGroup in groupedStatusesData) {
    if (xAxisBy === "date") {
      // accumulative totals for xAxisBy date
      const accumulativeDateTotals = {
        [ SupportTicketStatus.AdminResolved ]: 0,
        [ SupportTicketStatus.CustomerResolved ]: 0,
        [ SupportTicketStatus.New ]: 0,
        [ SupportTicketStatus.PendingAdminResponse ]: 0,
        [ SupportTicketStatus.PendingCustomerResponse ]: 0
      };

      groupedStatusesData[ xAxisGroup ].forEach(row => {
        accumulativeDateTotals[ row.status ] += row.count;
      });

      const dataItem: TotalTicketsByStatusGraphValue = {
        date: moment.utc(xAxisGroup).toDate(),
        // Only include statuses that have values
        ..._.pickBy(accumulativeDateTotals, value => value !== 0)
      };

      // Force header to include date day if displayRange not by month
      if (displayRange !== "month") {
        dataItem.toolTipHeader = moment.utc(xAxisGroup).format("Do MMMM YYYY");
      }

      result.push(dataItem);
    } else {
      // accumulative total for xAxisBy status
      let accumulativeTotal = 0;

      groupedStatusesData[ xAxisGroup ].forEach(row => {
        accumulativeTotal += row.count;
      });

      const dataItem: TotalTicketsByStatusGraphValue = {
        status: xAxisGroup as SupportTicketStatus,
        count: accumulativeTotal,
        toolTipHeader: _.startCase(xAxisGroup.replace(/[_-]/g, " "))
      };

      result.push(dataItem);
    }
  }

  return result;
};
